import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { newBatch, newBatchPhoto } from '../../utils/fieldBatches';
import { thumbnailOf } from '../../utils/fieldFrame';
import { localIsoWithOffset } from '../../utils/specimenCode';
import { exifFromFile } from '../../utils/exifGps';
import { isReadOnlyRefusal } from '../../utils/viewAs';
import AiFieldSuggestion from '../field/AiFieldSuggestion';

/*
 * A new observation (or collection) from a pin on the national map.
 *
 *   guidelines -> top -> underside -> habitat -> notes -> Save
 *
 * Saved the way Field Mode saves (utils/fieldBatches.js): a batch on THIS
 * DEVICE first, with its photos, then the outbox (utils/fieldOutbox.js) sends
 * it -- at once when there is a connection, otherwise when one returns. So an
 * observation made with no signal is never lost, nothing here re-implements
 * the retries, and the record arrives exactly as a Field Mode one would:
 *
 *   * an observation is a batch with details.observeOnly: no specimen code is
 *     minted (the map shows it as an observation, routes/map_points.py);
 *   * a collection is one without it: the outbox mints a code where the role may.
 *
 * Every photo is optional (CLAUDE.md, "Specimen photos are optional"); the three
 * slots are the guidance, not a gate. A photo picked from the device that
 * carries GPS in its EXIF moves the observation there, and says so, with a way
 * back to the pin. What its EXIF says of when and where it was taken, and how
 * surely, goes with the photo as its own tags (utils/exifGps.js).
 *
 * Made while a target observation is set on the map (`targetId`, utils/
 * activeTarget.js), the batch is stamped with it, as the camera stamps its own:
 * the record says which observation it documents.
 *
 * The notes step shows the photographs taken with "Identify with AI" beside
 * them (components/field/AiFieldSuggestion.js): a name the person accepts is
 * the record's identification, and the AI's answer goes with the batch, linked
 * to the record once the outbox has made it.
 */
export const SLOTS = [
  { key: 'top', view: 'cap_top' },
  { key: 'bottom', view: 'fertile_layer' },
  { key: 'habitat', view: 'habitat' },
];
const STEPS = ['guidelines', ...SLOTS.map(s => s.key), 'notes'];

const toLocalInput = (date) => {
  const pad = (n) => String(n).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
};

export default function ObservationWizard({ pin, mode = 'observation', store, outbox, userId, user = null,
  online = true, targetId = null, onClose, onSaved }) {
  const { t } = useTranslation(['map', 'common', 'viewAs']);
  const [step, setStep] = useState(0);
  const [photos, setPhotos] = useState({}); // slot key -> { file, url, gps, takenAt }
  const [location, setLocation] = useState({ lat: pin.lat, lng: pin.lng, from: 'pin' });
  const [when, setWhen] = useState(() => toLocalInput(new Date()));
  const [notes, setNotes] = useState('');
  // The AI's answer (AiFieldSuggestion) and a name the person accepted from it.
  const [ai, setAi] = useState(null);
  const [identified, setIdentified] = useState(null); // { taxon_name, rank }
  const [saving, setSaving] = useState(false);
  const [reading, setReading] = useState(0);
  const [error, setError] = useState('');
  const headingRef = useRef(null);
  const cameraRef = useRef(null);
  const uploadRef = useRef(null);

  useEffect(() => { headingRef.current?.focus?.(); }, [step]);
  const photosRef = useRef(photos);
  photosRef.current = photos;
  useEffect(() => () => Object.values(photosRef.current).forEach(p => p.url && URL.revokeObjectURL?.(p.url)), []);

  const slot = SLOTS.find(s => s.key === STEPS[step]);

  const choose = async (file) => {
    if (!file || !slot) return;
    // Save waits while a photo is being read, or a save tapped meanwhile
    // would go without it.
    setReading(n => n + 1);
    const meta = await exifFromFile(file).catch(() => null);
    setReading(n => n - 1);
    const gps = meta?.gps || null;
    setPhotos((all) => {
      if (all[slot.key]?.url) URL.revokeObjectURL?.(all[slot.key].url);
      return { ...all,
        [slot.key]: { file, url: URL.createObjectURL?.(file) || '', gps, takenAt: meta?.takenAt || null } };
    });
    // The first photo that knows where it was taken places the observation,
    // unless the person has already chosen a photo's place or kept the pin.
    if (gps && location.from === 'pin') setLocation({ lat: gps.lat, lng: gps.lng, from: 'photo' });
  };
  const removePhoto = () => setPhotos((all) => {
    const next = { ...all };
    if (next[slot.key]?.url) URL.revokeObjectURL?.(next[slot.key].url);
    delete next[slot.key];
    return next;
  });

  const save = async () => {
    setError('');
    const moment = new Date(when);
    if (Number.isNaN(moment.getTime())) return setError(t('wizard.whenRequired'));
    if (moment.getTime() > Date.now() + 60 * 1000) return setError(t('wizard.whenFuture'));
    setSaving(true);
    try {
      const batchPhotos = [];
      for (const s of SLOTS) {
        const chosen = photos[s.key];
        if (!chosen) continue;
        const photo = newBatchPhoto({ view: s.view, takenAt: chosen.takenAt, fix: chosen.gps });
        await store.putBlob(userId, photo.id, chosen.file);
        batchPhotos.push(photo);
      }
      const cover = SLOTS.map(s => photos[s.key]).find(Boolean);
      // A card picture only: failing to make one must not cost the observation.
      const thumbnail = cover ? await Promise.resolve().then(() => thumbnailOf(cover.file)).catch(() => null) : null;
      const batch = await store.save({
        ...newBatch({ userId, source: 'map', targetId }),
        status: 'pending',
        startedAt: localIsoWithOffset(moment),
        location: { lat: Number(location.lat), lng: Number(location.lng), accuracy_m: null },
        photos: batchPhotos,
        details: {
          observeOnly: mode === 'observation',
          general_notes: notes.trim() || undefined,
          ...(identified ? { taxon_name: identified.taxon_name, rank: identified.rank, confidence: 'uncertain' } : {}),
        },
        ...(ai?.aiId ? { ai } : {}),
        thumbnail,
        finalizedAt: new Date().toISOString(),
      });
      store.ensureDurable?.().catch(() => {});
      outbox.run();
      onSaved?.(batch, { queued: !online });
    } catch (e) {
      setSaving(false);
      setError(isReadOnlyRefusal(e) ? t('viewAs:notKept') : t('wizard.storeFailed'));
    }
    return undefined;
  };

  const back = () => (step === 0 ? onClose?.() : setStep(step - 1));
  const last = step === STEPS.length - 1;

  return (
    <div role="dialog" aria-modal="true" aria-labelledby="obs-wizard-title" style={sheet} data-testid="obs-wizard">
      <div style={sheetHeader}>
        <h3 id="obs-wizard-title" style={{ margin: 0, fontSize: 17 }}>
          {t(mode === 'collection' ? 'wizard.titleCollection' : 'wizard.titleObservation')}
        </h3>
        <button type="button" onClick={onClose} aria-label={t('common:close')} style={closeBtn}
          data-testid="obs-close">✕</button>
      </div>
      <ol style={stepper} aria-label={t('wizard.steps')}>
        {STEPS.map((s, i) => (
          <li key={s} aria-current={i === step ? 'step' : undefined}
            style={{ ...pill, ...(i === step ? pillOn : i < step ? pillDone : {}) }}>{t(`wizard.step.${s}`)}</li>
        ))}
      </ol>
      <h4 ref={headingRef} tabIndex={-1} style={{ margin: '10px 0' }} data-testid="obs-step">{t(`wizard.step.${STEPS[step]}`)}</h4>

      {STEPS[step] === 'guidelines' && (
        <div data-testid="obs-guidelines">
          <p style={{ marginTop: 0 }}>{t('guidelines.intro')}</p>
          <ul style={{ paddingInlineStart: 20, lineHeight: 1.6 }}>
            {['top', 'bottom', 'habitat', 'light', 'scale', 'leave'].map(k => <li key={k}>{t(`guidelines.${k}`)}</li>)}
          </ul>
          {!online && <p style={warn} data-testid="obs-offline">{t('wizard.offlineNote')}</p>}
        </div>
      )}

      {slot && (
        <div data-testid={`obs-slot-${slot.key}`}>
          <p style={muted}>{t(`slot.${slot.key}Hint`)}</p>
          <input ref={cameraRef} type="file" accept="image/*" capture="environment" hidden data-testid="obs-camera-input"
            onChange={(e) => { choose(e.target.files?.[0]); e.target.value = ''; }} />
          <input ref={uploadRef} type="file" accept="image/*" hidden data-testid="obs-upload-input"
            onChange={(e) => { choose(e.target.files?.[0]); e.target.value = ''; }} />
          {photos[slot.key] ? (
            <figure style={{ margin: 0 }}>
              {photos[slot.key].url && <img src={photos[slot.key].url} alt={t(`slot.${slot.key}`)} style={preview} />}
              {photos[slot.key].gps && <figcaption style={muted}>{t('wizard.photoHasGps')}</figcaption>}
            </figure>
          ) : <div style={emptySlot}>{t(`slot.${slot.key}`)}</div>}
          <div style={row}>
            <button type="button" onClick={() => cameraRef.current?.click()} style={btn('var(--c-primary)')}
              data-testid="obs-take">{photos[slot.key] ? t('slot.retake') : t('slot.take')}</button>
            <button type="button" onClick={() => uploadRef.current?.click()} style={btn('var(--c-info)')}
              data-testid="obs-upload">{t('slot.upload')}</button>
            {photos[slot.key] && (
              <button type="button" onClick={removePhoto} style={btn('var(--c-text-muted)')} data-testid="obs-remove">
                {t('slot.remove')}</button>
            )}
          </div>
        </div>
      )}

      {STEPS[step] === 'notes' && (
        <div style={{ display: 'grid', gap: 10 }}>
          {SLOTS.some(s => photos[s.key]) && (
            <div style={aiBox} data-testid="obs-ai">
              <div style={thumbs}>
                {SLOTS.filter(s => photos[s.key]).map(s => (photos[s.key].url
                  ? <img key={s.key} src={photos[s.key].url} alt={t(`slot.${s.key}`)} style={thumb} /> : null))}
              </div>
              <AiFieldSuggestion user={user} disabled={saving} value={ai} onChange={setAi} testid="obs-ai-suggest"
                hadOwnName={false} photos={SLOTS.filter(s => photos[s.key]).map(s => ({ file: photos[s.key].file, view: s.view }))}
                onAccept={(chosen) => setIdentified({ taxon_name: chosen.taxon_name, rank: chosen.rank })} />
              {identified && (
                <p style={{ ...muted, margin: 0 }} data-testid="obs-identified">
                  {t('wizard.identifiedAs', { taxon: identified.taxon_name })}
                  <button type="button" style={linkBtn} data-testid="obs-identified-clear"
                    onClick={() => setIdentified(null)}>{t('wizard.identifiedClear')}</button>
                </p>
              )}
            </div>
          )}
          <label style={label}>{t('wizard.notes')}
            <textarea rows={4} value={notes} onChange={(e) => setNotes(e.target.value)} style={input}
              data-testid="obs-notes" placeholder={t('wizard.notesPlaceholder')} />
          </label>
          <label style={label}>{t('wizard.when')}
            <input type="datetime-local" value={when} max={toLocalInput(new Date())} style={input}
              onChange={(e) => setWhen(e.target.value)} data-testid="obs-when" />
          </label>
        </div>
      )}

      <p style={{ ...muted, margin: '10px 0 0' }} data-testid="obs-location">
        {t(location.from === 'photo' ? 'wizard.locationPhoto' : 'wizard.locationPin',
          { latitude: Number(location.lat).toFixed(5), longitude: Number(location.lng).toFixed(5) })}
        {location.from === 'photo' && (
          <button type="button" style={linkBtn} data-testid="obs-use-pin"
            onClick={() => setLocation({ lat: pin.lat, lng: pin.lng, from: 'kept' })}>{t('wizard.usePin')}</button>
        )}
      </p>

      {error && <p role="alert" style={{ color: 'var(--c-danger-fg)' }}>{error}</p>}

      <div style={footer}>
        <button type="button" onClick={back} disabled={saving} style={btn('var(--c-text-muted)')} data-testid="obs-back">
          {step === 0 ? t('common:cancel') : t('wizard.back')}</button>
        {!last && (
          <button type="button" onClick={() => setStep(step + 1)} disabled={saving} style={btn('var(--c-primary)')}
            data-testid="obs-next">{slot && !photos[slot.key] ? t('wizard.skip') : t('wizard.next')}</button>
        )}
        {step > 0 && (
          <button type="button" onClick={save} disabled={saving || reading > 0} style={btn('var(--c-success)')} data-testid="obs-save">
            {saving ? t('common:saving') : t('wizard.save')}</button>
        )}
      </div>
    </div>
  );
}

// Fixed to the screen, not the map: on a phone the map box is short, and Back /
// Save must never be below the fold (the footer sticks to the sheet's bottom).
const sheet = { position: 'fixed', insetInline: 0, bottom: 0, zIndex: 1100, maxHeight: '92dvh', overflowY: 'auto',
  width: 'min(560px, 100%)', marginInline: 'auto', boxSizing: 'border-box',
  background: 'var(--c-surface)', color: 'var(--c-text-fg)', borderRadius: '14px 14px 0 0', padding: '16px 16px 0',
  boxShadow: '0 -6px 24px rgba(0,0,0,.25)' };
const footer = { position: 'sticky', bottom: 0, display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 14,
  padding: '10px 0 14px', background: 'var(--c-surface)', borderTop: '1px solid var(--c-border)' };
const sheetHeader = { display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8 };
const closeBtn = { background: 'none', border: 'none', fontSize: 20, cursor: 'pointer', color: 'var(--c-text-fg)', minWidth: 44, minHeight: 44 };
const stepper = { display: 'flex', gap: 6, flexWrap: 'wrap', listStyle: 'none', padding: 0, margin: '8px 0 0' };
const pill = { padding: '3px 9px', borderRadius: 999, fontSize: 12, border: '1px solid var(--c-border)', color: 'var(--c-text-muted-fg)' };
const pillOn = { background: 'var(--c-primary)', color: '#fff', borderColor: 'var(--c-primary)' };
const pillDone = { color: 'var(--c-text-fg)' };
const muted = { fontSize: 13, color: 'var(--c-text-muted-fg)' };
const warn = { fontSize: 13, color: 'var(--c-warning-fg)' };
const row = { display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 8 };
const preview = { display: 'block', maxWidth: '100%', maxHeight: 260, borderRadius: 8, objectFit: 'contain' };
const emptySlot = { display: 'flex', alignItems: 'center', justifyContent: 'center', height: 140, borderRadius: 8,
  border: '2px dashed var(--c-border)', color: 'var(--c-text-muted-fg)' };
const label = { display: 'flex', flexDirection: 'column', gap: 4, fontSize: 13, fontWeight: 600 };
const input = { padding: '8px 10px', borderRadius: 6, border: '1px solid var(--c-border)', fontSize: 15,
  background: 'var(--c-surface)', color: 'var(--c-text-fg)', boxSizing: 'border-box', width: '100%' };
const aiBox = { display: 'grid', gap: 8, padding: 10, borderRadius: 8, border: '1px solid var(--c-border)' };
const thumbs = { display: 'flex', gap: 6, flexWrap: 'wrap' };
const thumb = { width: 64, height: 64, objectFit: 'cover', borderRadius: 6 };
const linkBtn = { background: 'none', border: 'none', color: 'var(--c-primary)', textDecoration: 'underline',
  cursor: 'pointer', marginInlineStart: 8, padding: 0, fontSize: 13 };
const btn = (bg) => ({ padding: '10px 16px', minHeight: 44, borderRadius: 6, border: 'none', background: bg,
  color: '#fff', fontSize: 15, cursor: 'pointer' });
